<script context="module" lang="ts">
    import { get, writable } from "svelte/store"

    const cartErrorMessages = writable<string[]>([])
    function addCartErrorMessage(msg: string) {
        cartErrorMessages.update((msgs) => {
            msgs.push(msg)
            return msgs
        })
    }

    export function setValidationFailure(
        articleConfig: ArticleConfig,
        attrKey: string,
        f: string,
        fail: boolean
    ): string[] {
        if (!articleConfig.customAttributes) {
            articleConfig.customAttributes = {}
        }
        if (!articleConfig.customAttributes[attrKey]) {
            articleConfig.customAttributes[attrKey] = {}
        }
        if (!articleConfig.customAttributes[attrKey].validationFailures) {
            articleConfig.customAttributes[attrKey].validationFailures = []
        }
        const fArr = articleConfig.customAttributes[attrKey].validationFailures
        if (fail && !fArr.includes(f)) {
            fArr.push(f)
        } else if (!fail && fArr.includes(f)) {
            fArr.splice(fArr.indexOf(f), 1)
        }
        return fArr
    }

    let add2CartDialog: HTMLDialogElement

    // get cart from local storage
    const cartFromStorage = localStorage.getItem("cart")
    export const cart = writable<CartItem[]>(cartFromStorage ? JSON.parse(cartFromStorage) : [])

    // reflect cart to local storage
    cart.subscribe((value) => {
        localStorage.setItem("cart", JSON.stringify(value))
    })

    export function add2Cart(
        product: ProductEntry,
        quantity: number,
        productConfig?: ArticleConfig,
        bomItemConfigs?: ArticleConfig[]
    ) {
        if (productConfig) {
            // clone, so we don't change the original articleConfig
            productConfig = cloneDeep(productConfig)
        }
        if (bomItemConfigs) {
            // clone, so we don't change the original articleConfig
            bomItemConfigs = cloneDeep(bomItemConfigs)
        }
        const c = get(cart)
        const idx = c.findIndex((item) => {
            if (item.product.id !== product.id) return false

            // lets compare productConfig
            for (let attrKey in productConfig?.customAttributes) {
                const _aVal = item.productConfig?.customAttributes?.[attrKey]?.value
                const _bVal = productConfig?.customAttributes?.[attrKey]?.value
                if (_aVal != _bVal) {
                    // productConfig is different
                    return false
                }
            }

            // lets compare individual articleConfig items
            for (let i = 0; i < item.bomItemConfigs?.length; i++) {
                const _a = item.bomItemConfigs[i]
                const _b = bomItemConfigs[i]

                if (_a.article?.articleNumber !== _b.article?.articleNumber) {
                    // article is different
                    return false
                }

                // check customAttributes
                for (let attrKey in _b.customAttributes) {
                    const _aVal = _a.customAttributes?.[attrKey]?.value
                    const _bVal = _b.customAttributes?.[attrKey]?.value
                    if (_aVal != _bVal) {
                        // one of the customAttributes is different
                        return false
                    }
                }
            }

            if (product.externalData?.hasFixedQuantityOne) {
                // quantity is always 1, so add new item
                return false
            }

            // no differences found
            return true
        })

        if (idx < 0) {
            // generate id
            let cartId = product.id + "-" + Math.random().toString(36).substr(2, 9)
            c.push({ cartId, product, quantity, productConfig, bomItemConfigs: bomItemConfigs })
        } else {
            c[idx].quantity += quantity
        }

        cart.set(c)
        add2CartDialog.showModal()
    }

    export function removeCartItem(item: { product?: ProductEntry; cartId?: string }) {
        // find by fist by cartId and if not found by product.id
        const c = get(cart)
        let idx = c.findIndex((cartItem) => cartItem.cartId === item.cartId)
        if (idx < 0) {
            idx = c.findIndex((cartItem) => cartItem.product.id === item.product.id)
        }
        if (idx >= 0) {
            c.splice(idx, 1)
        }
        cart.set(c)
    }

    export function updateCartItem(item: { product?: ProductEntry; cartId?: string }, quantity: number) {
        const c = get(cart)
        let idx = c.findIndex((cartItem) => cartItem.cartId === item.cartId)
        if (idx < 0) {
            idx = c.findIndex((cartItem) => cartItem.product.id === item.product.id)
        }
        if (idx >= 0) {
            if (isNaN(quantity)) {
                quantity = 1
            }
            if (quantity <= 0) {
                c.splice(idx, 1)
            } else {
                c[idx].quantity = quantity
            }
        }
        cart.set(c)
    }

    export function duplicateSiidInCart(): string[] {
        let c = get(cart)
        const siids: string[] = []
        c.forEach((item) => {
            if (!item.bomItemConfigs) return
            item.bomItemConfigs.forEach((articleConfig) => {
                const siid = articleConfig.customAttributes?.["sportidentId"]?.value
                if (siid) siids.push(siid)
            })
        })

        // return multiple siids
        let duplicates = siids.filter((siid, idx) => siids.indexOf(siid) !== idx)
        // return unique siids
        duplicates = Array.from(new Set(duplicates))

        // mark items with duplicate siids
        c.forEach((item) => {
            if (!item.bomItemConfigs) return
            item.bomItemConfigs.forEach((articleConfig) => {
                if (!articleConfig.customAttributes?.["sportidentId"]) return
                setValidationFailure(
                    articleConfig,
                    "sportidentId",
                    "duplicate",
                    duplicates.includes(articleConfig.customAttributes["sportidentId"].value)
                )
            })
        })

        cart.set(c)
        return duplicates
    }

    export async function validateCart(): Promise<boolean> {
        cartErrorMessages.set([])

        let c = get(cart)
        if (!c?.length) return

        let changed = false
        let invalid = false

        // get all products from cart
        const productIds = c.map((item) => item.product.id)
        const products = await getDBEntries("product", { _id: { $in: productIds } }, null, null, null, null, {
            si: "2",
        })

        // filter out not available products
        const availableProducts = products.filter((product) => product.externalData?.isAvailable)
        c = c.filter((item) => {
            const found = availableProducts.find((product) => product.id === item.product.id)
            if (!found) {
                changed = true
                console.log("Product not available anymore", item)
                return false
            }
            return true
        })

        // map product data from database to cart items
        c = c.map((item) => {
            const product = availableProducts.find((product) => product.id === item.product.id)
            if (!isEqual(item.product, product)) {
                changed = true
                item.changedByValidation = true
                console.log("Product data changed for", item)
            }

            // check quantity
            if (item.quantity != 1 && item.product.externalData?.hasFixedQuantityOne) {
                item.quantity = 1
                changed = true
                item.changedByValidation = true
                console.log("Quantity set to 1 because of fixed quantity one for", item)
            }
            return { ...item, product }
        })

        // validate articleConfig
        c = c.map((item) => {
            if (!item.bomItemConfigs?.length) return item
            const validArticleConfigs: ArticleConfig[] = []
            item.bomItemConfigs.forEach((articleConfig, idx) => {
                // check if articleConfig.article is in validArticles
                const validArticles = item.product.externalData.bomItems?.[idx]?.validArticles

                if (!validArticles?.length) {
                    validArticleConfigs.push(articleConfig)
                    return
                }

                const validArticle = validArticles.find(
                    (article) => article.articleNumber === articleConfig.article?.articleNumber
                )

                if (validArticle) {
                    validArticleConfigs.push(articleConfig)
                } else {
                    // use first
                    validArticleConfigs.push({
                        article: item.product.externalData.bomItems[idx]?.validArticles[0],
                        customAttributes: articleConfig.customAttributes,
                        valid: articleConfig.valid,
                    })
                }
            })

            // check articleConfig for changes, but ignore customAttributes
            for (let i = 0; i < item.bomItemConfigs.length; i++) {
                if (!isEqual(item.bomItemConfigs[i].article, validArticleConfigs[i].article)) {
                    changed = true
                    item.changedByValidation = true
                    console.log("articleConfig " + i + " changed for", item)
                    break
                }
            }
            return { ...item, bomItemConfigs: validArticleConfigs }
        })

        // validate customAttributes
        for (let cartIdx = 0; cartIdx < c.length; cartIdx++) {
            const item = c[cartIdx]
            if (!item.bomItemConfigs?.length) continue

            for (let bomIdx = 0; bomIdx < item.bomItemConfigs.length; bomIdx++) {
                const articleConfig = item.bomItemConfigs[bomIdx]

                for (
                    let customAttrIdx = 0;
                    customAttrIdx < item.bomItemConfigs[bomIdx].article?.customAttributes?.length;
                    customAttrIdx++
                ) {
                    const _aConfig = item.bomItemConfigs[bomIdx].article.customAttributes[customAttrIdx]
                    const _key = _aConfig.key

                    // check if ok with quantity
                    const quantity =
                        item.quantity * (item.product?.externalData?.bomItems?.[bomIdx]?.orderItem?.quantity || 1)
                    if (
                        (!_aConfig.applicableToQuantityOne && quantity == 1) ||
                        (!_aConfig.applicableToQuantityGreaterThanOne && quantity > 1)
                    ) {
                        // remove attribute
                        delete articleConfig.customAttributes[_key]
                        continue
                    }

                    const bomItem = item.product?.externalData?.bomItems?.[bomIdx]
                    validateCustomAttribute(
                        articleConfig,
                        _aConfig,
                        bomItem?.orderItem?.configuration,
                        bomItem?.excludedCustomAttributes
                    )

                    // check sportidentId here and not in validateCustomAttribute because only check if really needed
                    if (_key === "sportidentId" && !bomItem?.excludedCustomAttributes?.includes(_key)) {
                        const v = articleConfig.customAttributes?.[_key]?.value

                        if (v) {
                            const r = await checkSiid(articleConfig.article?.properties?.cardType, v)

                            setValidationFailure(articleConfig, _key, "invalid", !r.isValid)
                            setValidationFailure(articleConfig, _key, "inUse", r.isValid && !r.canBeOrdered)
                        } else {
                            setValidationFailure(articleConfig, _key, "invalid", false)
                            setValidationFailure(articleConfig, _key, "inUse", false)
                        }
                    }

                    const validationFailures = articleConfig.customAttributes[_key]?.validationFailures
                    if (validationFailures?.length) {
                        console.log(
                            "Validation failures for",
                            cartIdx,
                            item,
                            _key,
                            articleConfig.customAttributes[_key]?.validationFailures
                        )
                        invalid = true
                    }
                }
            }
        }

        cart.set(c)

        if (changed) {
            addCartErrorMessage("Some items in your cart have changed.")
        }

        if (invalid) {
            addCartErrorMessage("Some items in your cart have validation errors.")
        }

        // check for duplicate siids
        const duplicateSiids = duplicateSiidInCart()
        if (duplicateSiids?.length) {
            addCartErrorMessage("Duplicate Sportident numbers in cart.")
            invalid = true
        }

        if (invalid || changed) {
            return false
        }

        return true
    }
</script>

<script lang="ts">
    import { spaLink, spaNavigate } from "../actions"
    import { location } from "../store"
    import { cloneDeep, isEqual } from "lodash"
    import { getDBEntries } from "../../api"
    import { checkSiid, validateCustomAttribute } from "./ProductConfiguratorCustomAttributes.svelte"

    $: countOfCartItems = $cart?.reduce((acc, item) => acc + item.quantity, 0) || 0

    let cartErrorDialog: HTMLDialogElement
    $: if (cartErrorDialog && $cartErrorMessages.length) {
        cartErrorDialog.showModal()
    }
</script>

<a
    use:spaLink
    href="/cart"
    class="cta btn-primary"
    class:gray="{countOfCartItems === 0}"
    class:active="{$location.path === '/cart'}"
    aria-label="Shopping cart with {countOfCartItems} items"
>
    {#if countOfCartItems}
        <span class="badge">{countOfCartItems}</span>
    {/if}
    <img src="/assets/icons/lucide_shopping-cart.svg" alt="Cart" />
</a>

<dialog class="smooth-shadow border-radius-all border-radius-m" bind:this="{add2CartDialog}">
    <div class="container">
        <div class="grid justify-center align-center">
            <div class="col-12">
                <h2 class="h3">Product added to cart</h2>
                <!-- TODO: ADD PRODUCT NAME / IMAGE / INFO? -->
            </div>
            <div class="col-12">
                <button
                    class="cta btn-primary btn-shopping"
                    on:click="{() => {
                        add2CartDialog.close()
                    }}">Continue shopping</button
                >
                <button
                    class="cta btn-primary btn-cardReservation"
                    on:click="{() => {
                        spaNavigate('/cart')
                        add2CartDialog.close()
                    }}">Go to cart</button
                >
            </div>
        </div>
    </div>
</dialog>

<dialog class="smooth-shadow border-radius-all border-radius-m" bind:this="{cartErrorDialog}">
    <div class="container">
        <div class="grid justify-center align-center">
            <div class="col-12">
                <h2 class="h3">Please review your cart</h2>
                <ul>
                    {#each $cartErrorMessages as message}
                        <li>{message}</li>
                    {/each}
                </ul>
            </div>
            <div class="col-12">
                <button
                    class="cta btn-primary btn-cardReservation"
                    on:click="{() => {
                        if ($location.path !== '/cart') {
                            spaNavigate('/cart')
                        }
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                        cartErrorDialog.close()
                    }}">Go to cart</button
                >
            </div>
        </div>
    </div>
</dialog>
